import { Link } from "react-router-dom";
import { Leaf, Mail, Phone, MapPin, Instagram, Linkedin, ArrowUpRight } from "lucide-react";
import { SITE, NAV_LINKS } from "@/data/content";

const SOCIALS = [
  { name: "Instagram", url: "https://www.instagram.com/jencullertonjohnson/", icon: Instagram },
  { name: "LinkedIn", url: "https://www.linkedin.com/in/jen-cullerton-johnson-a278ba9b", icon: Linkedin },
  { name: "Substack", url: "https://substack.com/@greenliteracy", icon: null },
  { name: "Green Literacy", url: "https://www.greenliteracy.org/", icon: null },
];

export default function Footer() {
  return (
    <footer className="bg-ink text-paper relative grain overflow-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-20 pb-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gold mb-6">Planting seeds of change</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[1.05] mb-8">
              Bring Jen to your classroom, <em className="text-gold">library,</em> or forest.
            </h2>
            <Link
              to="/collaborate"
              className="inline-flex items-center gap-2 bg-terra hover:bg-terradark text-paper rounded-full px-7 py-3.5 font-medium transition-colors duration-300"
              data-testid="footer-book-visit-btn"
            >
              Book an Author Visit <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-paper/60 mb-5">Explore</p>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="link-underline text-paper/85 hover:text-paper text-sm" data-testid={`footer-link-${l.to.replace("/", "")}`}>
                    {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-paper/60 mb-5">Reach out</p>
            <ul className="space-y-3 text-sm text-paper/85">
              <li>
                <a href={`mailto:${SITE.email}`} className="link-underline inline-flex items-center gap-2" data-testid="footer-email-link">
                  <Mail className="w-4 h-4 text-gold" /> {SITE.email}
                </a>
              </li>
              <li>
                <a href={SITE.phoneHref} className="link-underline inline-flex items-center gap-2" data-testid="footer-phone-link">
                  <Phone className="w-4 h-4 text-gold" /> {SITE.phone}
                </a>
              </li>
              <li className="inline-flex items-center gap-2">
                <MapPin className="w-4 h-4 text-gold" /> {SITE.location}
              </li>
            </ul>
            <div className="flex items-center gap-3 mt-6">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  title={s.name}
                  className="w-9 h-9 rounded-full border border-paper/25 flex items-center justify-center text-paper/80 hover:bg-terra hover:border-terra hover:text-paper transition-colors duration-300 text-[10px] font-mono"
                  data-testid={`footer-social-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {s.icon ? <s.icon className="w-4 h-4" /> : s.name.slice(0, 2).toUpperCase()}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-paper/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-paper/50 text-xs font-mono flex items-center gap-2">
            <Leaf className="w-3.5 h-3.5 text-gold" /> © {new Date().getFullYear()} Jen Cullerton Johnson. All rights reserved.
          </p>
          <p className="text-paper/50 text-xs font-mono">Author · Educator · Forest Guide — Chicago</p>
        </div>
      </div>
    </footer>
  );
}
